import type { ExternalSqlProvider, ProductMode } from './productMode';

const ENVIRONMENT_SETTINGS_KEY = 'revenue:environment_settings';
const EXTERNAL_SQL_SESSION_PASSWORD_KEY = 'revenue:external_sql_session_password';

export interface DatabaseConnectionSettings {
  mode: ProductMode;
  provider: ExternalSqlProvider;
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  connectionName: string;
  encryptedConnection: boolean;
}

export interface EnvironmentSettings {
  database: DatabaseConnectionSettings;
}

export const defaultEnvironmentSettings: EnvironmentSettings = {
  database: {
    mode: 'standalone',
    provider: 'postgresql',
    host: '',
    port: '',
    database: '',
    username: '',
    password: '',
    connectionName: '',
    encryptedConnection: true,
  },
};

export interface ExternalSqlConnectionPayload {
  provider: ExternalSqlProvider;
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  encryptedConnection: boolean;
}

function loadSessionPassword(): string {
  try {
    return sessionStorage.getItem(EXTERNAL_SQL_SESSION_PASSWORD_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveSessionPassword(password: string): void {
  try {
    if (password) {
      sessionStorage.setItem(EXTERNAL_SQL_SESSION_PASSWORD_KEY, password);
    } else {
      sessionStorage.removeItem(EXTERNAL_SQL_SESSION_PASSWORD_KEY);
    }
  } catch {
    // Session-only password storage is best-effort; validation will catch missing values.
  }
}

export function loadEnvironmentSettings(): EnvironmentSettings {
  try {
    const raw = localStorage.getItem(ENVIRONMENT_SETTINGS_KEY);
    if (!raw) return defaultEnvironmentSettings;
    const parsed = JSON.parse(raw) as Partial<EnvironmentSettings>;
    return {
      database: {
        ...defaultEnvironmentSettings.database,
        ...(parsed.database ?? {}),
        password: loadSessionPassword(),
      },
    };
  } catch {
    return {
      database: {
        ...defaultEnvironmentSettings.database,
        password: loadSessionPassword(),
      },
    };
  }
}

export function saveEnvironmentSettings(settings: EnvironmentSettings): void {
  const { password, ...database } = settings.database;
  saveSessionPassword(password);
  localStorage.setItem(ENVIRONMENT_SETTINGS_KEY, JSON.stringify({
    ...settings,
    database,
  }));
}

export function validateDatabaseSettings(settings: DatabaseConnectionSettings): string | null {
  if (settings.mode === 'demo') return 'デモモードではDB接続は使用できません';
  if (settings.mode === 'standalone') return null;
  if (settings.mode === 'server-client') {
    if (!settings.provider) return 'SQL種別を選択してください';
    if (settings.provider !== 'postgresql' && settings.provider !== 'mysql') {
      return '外部DB接続はPostgreSQL/MySQLのみ利用できます。MS SQL Server/Oracle/ODBCは準備中です';
    }
    if (!settings.host.trim()) return 'ホストを入力してください';
    if (!settings.database.trim()) return 'データベース名を入力してください';
    if (!settings.username.trim()) return 'ユーザー名を入力してください';
    if (!settings.password) return 'DBパスワードを入力してください';
    return null;
  }
  return 'モードを選択してください';
}

export function externalSqlConnectionPayload(settings = loadEnvironmentSettings().database): ExternalSqlConnectionPayload {
  return {
    provider: settings.provider,
    host: settings.host.trim(),
    port: settings.port.trim(),
    database: settings.database.trim(),
    username: settings.username.trim(),
    password: settings.password,
    encryptedConnection: settings.encryptedConnection,
  };
}
